import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';

import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import TextArea from '@/components/TextArea';
import { useFormatText } from '@/hooks/useFormatText';
import {
  type TCreateLessonForm,
  schoolCreateLessonSchema,
  teacherCreateLessonSchema,
} from '@/schema/createLessonSchema';
import {
  getClassDropdown,
  getTeacherClassDropdown,
} from '@/service/classService';
import {
  createLesson,
  getLessonStudentDropdown,
  getLessonTeacherDropdown,
  getTeacherStudentsDropdown,
} from '@/service/lessonService';
import { IGamePayload, ICreateLessonPayload } from '@/types/payload';
import { formatQuery } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

interface IDropdownOption {
  value: string;
  label: string;
}

interface ICreateLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleType: 'teacher' | 'school';
  selectedGames: IGamePayload[];
  preselectedClassId?: string;
  onSuccess?: () => void;
}

const CreateLessonModal = ({
  isOpen,
  onClose,
  moduleType,
  selectedGames,
  preselectedClassId,
  onSuccess,
}: ICreateLessonModalProps) => {
  const { formatText } = useFormatText();
  const isSchool = moduleType === 'school';

  const schema = useMemo(
    () => (isSchool ? schoolCreateLessonSchema : teacherCreateLessonSchema),
    [isSchool]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classOptions, setClassOptions] = useState<IDropdownOption[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<IDropdownOption[]>([]);
  const [studentOptions, setStudentOptions] = useState<IDropdownOption[]>([]);
  const [isTeacherLoading, setIsTeacherLoading] = useState(false);
  const [isStudentLoading, setIsStudentLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TCreateLessonForm>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      title: '',
      description: '',
      classId: preselectedClassId ?? '',
      dueDate: '',
      studentAssigned: [],
      instructionsForStudents: '',
      ...(isSchool ? { teacherId: '' } : {}),
    },
  });

  const selectedClassId = useWatch({ control, name: 'classId' });

  // Lock the class dropdown when a class is preselected (single-select)
  useEffect(() => {
    if (!isOpen) return;
    if (!preselectedClassId) return;

    setValue('classId', preselectedClassId, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [isOpen, preselectedClassId, setValue]);

  // ── Fetch class options ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const fetchClasses = async () => {
      try {
        const res = isSchool
          ? await getClassDropdown(true)
          : await getTeacherClassDropdown(true);
        if (res?.data) {
          setClassOptions(
            res.data.map((item: { id: string; name: string }) => ({
              value: item.id,
              label: item.name,
            }))
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchClasses();
  }, [isOpen, isSchool]);

  // ── Fetch teachers + students when class changes ────────────────────────
  useEffect(() => {
    if (!selectedClassId) {
      setTeacherOptions([]);
      setStudentOptions([]);
      setValue('studentAssigned', []);
      if (isSchool) setValue('teacherId', '');
      return;
    }

    const fetchDependents = async () => {
      setValue('studentAssigned', []);
      if (isSchool) setValue('teacherId', '');

      // Teachers (school only)
      if (isSchool) {
        setIsTeacherLoading(true);
        try {
          const res = await getLessonTeacherDropdown([selectedClassId], true);
          if (res?.data) {
            setTeacherOptions(
              res.data.map((item: { id: string; name: string }) => ({
                value: item.id,
                label: item.name,
              }))
            );
          }
        } catch (error) {
          console.error(error);
        } finally {
          setIsTeacherLoading(false);
        }
      }

      // Students
      setIsStudentLoading(true);
      try {
        if (isSchool) {
          const res = await getLessonStudentDropdown([selectedClassId], true);
          if (res?.data) {
            setStudentOptions(
              res.data.map((item: { id: string; name: string }) => ({
                value: item.id,
                label: item.name,
              }))
            );
          }
        } else {
          const params = { class_id: [selectedClassId], exclude_block: true };
          const qs = formatQuery(params);
          const res = await getTeacherStudentsDropdown(qs);
          if (res?.data) {
            setStudentOptions(
              res.data.map((item: { id: string; name: string }) => ({
                value: item.id,
                label: item.name,
              }))
            );
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsStudentLoading(false);
      }
    };

    fetchDependents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const onSubmit = async (data: TCreateLessonForm) => {
    setIsSubmitting(true);
    try {
      const payload: ICreateLessonPayload = {
        title: data.title,
        assign_date: Math.floor(Date.now() / 1000),
        due_date: Math.floor(new Date(data.dueDate).getTime() / 1000),
        class_id: data.classId,
        student_ids: data.studentAssigned,
        games: selectedGames,
      };

      if (data.description) {
        payload.description = data.description;
      }
      if (data.instructionsForStudents) {
        payload.instructions_for_students = data.instructionsForStudents;
      }

      if (isSchool && data.teacherId) {
        payload.teacher_id = data.teacherId;
      }

      const res = await createLesson(payload);

      if (res?.status) {
        enqueueSnackbar(res.message || formatText('common.success'), {
          variant: 'success',
        });
        reset();
        onClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(res?.message || formatText('common.error'), {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keep the form mounted — only hide the modal visually
  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={onClose}
      isHeader
      heading={formatText('lessons.create-drawer.title')}
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('lessons.create-drawer.cancel'),
        variant: 'link',
        onClick: onClose,
      }}
      confirmButtonProps={{
        label: formatText('lessons.create-drawer.submit'),
        variant: 'primary',
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        disabled: isSubmitting,
      }}
    >
      <form
        data-testid="create-lesson-form"
        className="flex flex-col gap-5 w-full"
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Title */}
        <Input
          name="title"
          label={formatText('lessons.create-drawer.fields.title')}
          placeholder={formatText(
            'lessons.create-drawer.fields.title-placeholder'
          )}
          register={register}
          required
          dataTestId="lesson-title-input"
          error={
            errors.title?.message ? formatText(errors.title.message) : undefined
          }
        />

        {/* Learning Objective */}
        <Input
          name="description"
          label={formatText('lessons.create-drawer.fields.learning-objective')}
          placeholder={formatText(
            'lessons.create-drawer.fields.learning-objective-placeholder'
          )}
          register={register}
          // required
          dataTestId="lesson-description-input"
          error={
            errors.description?.message
              ? formatText(errors.description.message)
              : undefined
          }
        />

        {/* Due Date */}
        <Controller
          name="dueDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={formatText('lessons.create-drawer.fields.due-date')}
              value={field.value || null}
              onChange={(date) => field.onChange(date ?? '')}
              placeholder={formatText(
                'lessons.create-drawer.fields.due-date-placeholder'
              )}
              min={new Date().toISOString().split('T')[0]}
              isRequired
              dataTestId="lesson-due-date-picker"
              error={
                errors.dueDate?.message
                  ? formatText(errors.dueDate.message)
                  : undefined
              }
            />
          )}
        />

        {/* Class */}
        <Controller
          name="classId"
          control={control}
          render={({ field }) => (
            <Select
              name={formatText('lessons.create-drawer.fields.class-assigned')}
              options={classOptions}
              selected={field.value}
              onChange={(val) => {
                if (preselectedClassId) return;
                field.onChange(Array.isArray(val) ? val[0] || '' : val);
              }}
              placeholder={formatText(
                'lessons.create-drawer.fields.class-assigned-placeholder'
              )}
              isRequired
              isClearable={!preselectedClassId}
              disabled={!!preselectedClassId}
              dataTestId="lesson-class-select"
              error={
                errors.classId?.message
                  ? formatText(errors.classId.message)
                  : undefined
              }
            />
          )}
        />

        {/* Teacher (school only) */}
        {isSchool && (
          <Controller
            name="teacherId"
            control={control}
            render={({ field }) => (
              <Select
                name={formatText(
                  'lessons.create-drawer.fields.teacher-assigned'
                )}
                options={teacherOptions}
                selected={field.value as string}
                onChange={(val) =>
                  field.onChange(Array.isArray(val) ? val[0] || '' : val)
                }
                placeholder={formatText(
                  'lessons.create-drawer.fields.teacher-assigned-placeholder'
                )}
                isRequired
                isClearable
                disabled={!selectedClassId || isTeacherLoading}
                dataTestId="lesson-teacher-select"
                error={
                  errors.teacherId?.message
                    ? formatText(errors.teacherId.message)
                    : undefined
                }
              />
            )}
          />
        )}

        {/* Students */}
        <Controller
          name="studentAssigned"
          control={control}
          render={({ field }) => (
            <Select
              name={formatText('lessons.create-drawer.fields.student-assigned')}
              options={studentOptions}
              selected={field.value}
              onChange={(val) => field.onChange(val)}
              placeholder={formatText(
                'lessons.create-drawer.fields.student-assigned-placeholder'
              )}
              isMulti
              isSearch
              isRequired
              isClearable
              disabled={!selectedClassId || isStudentLoading}
              dataTestId="lesson-student-select"
              error={
                errors.studentAssigned?.message
                  ? formatText(errors.studentAssigned.message)
                  : undefined
              }
            />
          )}
        />

        {/* Instructions for Students (optional) */}
        <TextArea
          register={register}
          name="instructionsForStudents"
          label={formatText(
            'lessons.create-drawer.fields.instructions-for-students'
          )}
          placeholder={formatText(
            'lessons.create-drawer.fields.instructions-for-students-placeholder'
          )}
          rows={3}
        />
      </form>
    </CustomModal>
  );
};

export default CreateLessonModal;
